import { Position } from '@xyflow/react';
import nodeData from '../data/nodes.json';
import linkData from '../data/links.json';
import contentData from '../data/content.json';

// aiNews는 content.json에서 type이 'news'인 항목들을 필터링
export const aiNews = contentData.filter(item => item.type === 'news');

// recentArticles는 content.json에서 유튜브를 제외한 모든 콘텐츠를 필터링
export const recentArticles = contentData.filter(item => item.type !== 'youtube');

export { nodeData, linkData };

// [FEEDBACK] Radial Layout: 8 Directions (with special North handling for Programming)
export const directionMap = {
    "General AI": "North",
    "Development & Programming": "North-Left", // Special: Share North handle with General AI, positioned left
    "Education": "NorthEast",
    "Productivity & Collaboration": "East",
    "Business & Marketing": "SouthEast",
    "Search & Data": "SouthWest",
    "Entertainment & Creative": "West",
    "Miscellaneous": "NorthWest"
};

// [FEEDBACK] Category Colors
export const categoryColors = {
    "General AI": "from-gray-500 to-slate-600",
    "Education": "from-emerald-400 to-teal-600",
    "Productivity & Collaboration": "from-orange-400 to-amber-600",
    "Business & Marketing": "from-blue-400 to-indigo-600",
    "Development & Programming": "from-violet-500 to-purple-700",
    "Search & Data": "from-cyan-400 to-blue-600",
    "Entertainment & Creative": "from-pink-400 to-rose-600",
    "Miscellaneous": "from-lime-400 to-green-600"
};

const DIRECTIONS = {
    North: { angle: -90, xDir: 0, yDir: -1, source: Position.Top, target: Position.Bottom },
    "North-Left": { angle: -90, xDir: -0.7, yDir: -1, source: Position.Top, target: Position.Bottom, isSpecial: true }, // Special for Programming
    NorthEast: { angle: -45, xDir: 1, yDir: -1, source: Position.Right, target: Position.Left },
    East: { angle: 0, xDir: 1, yDir: 0, source: Position.Right, target: Position.Left },
    SouthEast: { angle: 45, xDir: 1, yDir: 1, source: Position.Right, target: Position.Left },
    South: { angle: 90, xDir: 0, yDir: 1, source: Position.Bottom, target: Position.Top },
    SouthWest: { angle: 135, xDir: -1, yDir: 1, source: Position.Left, target: Position.Right },
    West: { angle: 180, xDir: -1, yDir: 0, source: Position.Left, target: Position.Right },
    NorthWest: { angle: 225, xDir: -1, yDir: -1, source: Position.Left, target: Position.Right }
};

export function calculateLayout() {
    const nodeMap = new Map(nodeData.map(n => [n.id, n]));
    const positionedNodes = new Map();

    // Layout Constants
    const R1 = 900; // Distance from Center to G1
    const R1_NORTH = 1050; // Increased distance for North nodes (General AI, Programming)
    const G2_SPACING = 140; // Spacing between G2 nodes
    const GRID_OFFSET = 250; // Distance from G2 to Grid (reduced from 400)

    // 0. Root Node (Upper offset to shift view up)
    const aiNodeData = nodeMap.get("AI");
    if (aiNodeData) {
        positionedNodes.set("AI", { id: "AI", type: 'mindmap', data: { ...aiNodeData }, position: { x: -150, y: -250 }, draggable: false });
    }

    // 1. Place G1 Nodes (8 Directions)
    Object.entries(directionMap).forEach(([g1Id, dirKey]) => {
        const nodeInfo = nodeMap.get(g1Id);
        if (!nodeInfo) return;

        const dirConfig = DIRECTIONS[dirKey];
        const rad = dirConfig.angle * (Math.PI / 180);

        let g1Pos;

        // Special handling for North-Left (Programming node)
        if (dirKey === "North-Left") {
            // Position to the left of center, symmetric with General AI on the right
            g1Pos = {
                x: -450, // Left of center (symmetric)
                y: Math.sin(rad) * R1_NORTH // Same Y as General AI
            };
        } else if (dirKey === "North") {
            // General AI - positioned to the right of center for symmetry
            g1Pos = {
                x: 450, // Right of center (symmetric)
                y: Math.sin(rad) * R1_NORTH
            };
        } else {
            g1Pos = {
                x: Math.cos(rad) * R1,
                y: Math.sin(rad) * R1
            };
        }

        // Determine Handles for G1
        // G1 Target (from Root): Depends on direction. 
        // Root is at (0,0). 
        // N/N-Left: Bottom, S: Top, E/W/Diagonals: Left/Right opposite to direction
        let g1TargetPos = Position.Left; // Default
        if (dirKey === 'North' || dirKey === 'North-Left') g1TargetPos = Position.Bottom;
        else if (dirKey === 'South') g1TargetPos = Position.Top;
        else if (dirConfig.xDir > 0) g1TargetPos = Position.Left;
        else if (dirConfig.xDir < 0) g1TargetPos = Position.Right;

        // G1 Source (to G2): 
        // N/N-Left: Top, S: Bottom, E/W/Diagonals: Right/Left matching direction
        let g1SourcePos = Position.Right; // Default
        if (dirKey === 'North' || dirKey === 'North-Left') g1SourcePos = Position.Top;
        else if (dirKey === 'South') g1SourcePos = Position.Bottom;
        else if (dirConfig.xDir > 0) g1SourcePos = Position.Right;
        else if (dirConfig.xDir < 0) g1SourcePos = Position.Left;


        // 2. Place G2 Nodes
        const g2Nodes = linkData.filter(l => l.source === g1Id).map(l => l.target);

        // Calculate G2 Layout Block
        // For N/N-Left/S: Horizontal Row
        // For Others: Vertical Column
        const isHorizontalStack = dirKey === 'North' || dirKey === 'North-Left' || dirKey === 'South';

        // [FEEDBACK] Increased spacing for General AI (North) and Programming (North-Left)
        const currentG2Spacing = isHorizontalStack ? 280 : G2_SPACING;

        const totalSize = (g2Nodes.length - 1) * currentG2Spacing;
        const startOffset = -totalSize / 2;

        g2Nodes.forEach((g2Id, index) => {
            const g2NodeInfo = nodeMap.get(g2Id);
            if (!g2NodeInfo) return;

            let g2Pos = { x: 0, y: 0 };
            const offset = startOffset + (index * currentG2Spacing);

            if (isHorizontalStack) {
                // Stack Horizontally
                // N/N-Left: Above G1, S: Below G1
                const yOffset = (dirKey === 'North' || dirKey === 'North-Left') ? -300 : 300;
                g2Pos = {
                    x: g1Pos.x + offset,
                    y: g1Pos.y + yOffset
                };
            } else {
                // Stack Vertically
                // E/NE/SE: Right of G1 (increased spacing to 550)
                // W/NW/SW: Left of G1 (keep at 400)
                const xOffset = dirConfig.xDir > 0 ? 550 : -400;
                g2Pos = {
                    x: g1Pos.x + xOffset,
                    y: g1Pos.y + offset
                };
            }

            // G2 Handles
            let g2TargetPos = isHorizontalStack
                ? ((dirKey === 'North' || dirKey === 'North-Left') ? Position.Bottom : Position.Top)
                : (dirConfig.xDir > 0 ? Position.Left : Position.Right);

            let g2SourcePos = isHorizontalStack
                ? ((dirKey === 'North' || dirKey === 'North-Left') ? Position.Top : Position.Bottom)
                : (dirConfig.xDir > 0 ? Position.Right : Position.Left);

            const g2Data = {
                ...g2NodeInfo,
                label: g2NodeInfo.label || g2Id,
                targetPosition: g2TargetPos,
                sourcePosition: g2SourcePos
            };

            positionedNodes.set(g2Id, { id: g2Id, type: 'mindmap', data: g2Data, position: g2Pos, draggable: false });

            // 3. Place G3 Nodes (Grid)
            const g3NodesData = linkData.filter(l => l.source === g2Id).map(l => nodeMap.get(l.target)).filter(Boolean);
            if (g3NodesData.length > 0) {
                const gridNodeId = `grid-${g2Id}`;
                // [FEEDBACK] Pass ALL services to allow pagination in the component
                // const limitedServices = g3NodesData.slice(0, 8); // REMOVED slicing here

                let gridPos = { x: 0, y: 0 };

                if (isHorizontalStack) {
                    // N/N-Left: Above G2, S: Below G2
                    // General AI (North) and Programming (North-Left): increase spacing from 250 to 320
                    const currentGridOffset = (dirKey === 'North' || dirKey === 'North-Left') ? 320 : GRID_OFFSET;
                    const gridYOffset = (dirKey === 'North' || dirKey === 'North-Left') ? -currentGridOffset : currentGridOffset;
                    gridPos = {
                        x: g2Pos.x - 170, // Center grid (width 340)
                        y: g2Pos.y + gridYOffset
                    };
                } else {
                    // E: Right of G2, W: Left of G2
                    // Left side: reduce spacing from 250 to 180
                    // Right side: keep at 250
                    const currentGridOffset = dirConfig.xDir > 0 ? GRID_OFFSET : 180;
                    const gridXOffset = dirConfig.xDir > 0 ? currentGridOffset : -currentGridOffset - 340; // Adjust for width
                    gridPos = {
                        x: g2Pos.x + gridXOffset,
                        y: g2Pos.y - 100 // Center vertically relative to G2
                    };
                }

                const gridData = {
                    services: g3NodesData, // Pass FULL list
                    targetPosition: g2SourcePos === Position.Right ? Position.Left :
                        g2SourcePos === Position.Left ? Position.Right :
                            g2SourcePos === Position.Top ? Position.Bottom : Position.Top,
                    totalCount: g3NodesData.length
                };

                positionedNodes.set(gridNodeId, { id: gridNodeId, type: 'serviceGrid', data: gridData, position: gridPos, draggable: false });
            }
        });

        const g1Data = { ...nodeInfo, label: nodeInfo.label || g1Id, targetPosition: g1TargetPos, sourcePosition: g1SourcePos };
        positionedNodes.set(g1Id, { id: g1Id, type: 'mindmap', data: g1Data, position: g1Pos, draggable: false });
    });

    return Array.from(positionedNodes.values());
}

export function createEdges() {
    let edges = [];

    // Helper to get direction config for a node
    const getDirConfig = (nodeId) => {
        // Check if G1
        if (directionMap[nodeId]) return DIRECTIONS[directionMap[nodeId]];
        // Check if G2/G3 (find parent G1)
        // This is expensive but necessary if we don't pass context. 
        // Optimization: We can infer from position or just look up linkData.
        const link = linkData.find(l => l.target === nodeId);
        if (link) return getDirConfig(link.source);
        return null;
    };

    linkData.forEach(link => {
        const sourceNode = nodeData.find(n => n.id === link.source);
        const targetNode = nodeData.find(n => n.id === link.target);

        if (!sourceNode || !targetNode) return;

        // G0 -> G1
        if (sourceNode.group === 0 && targetNode.group === 1) {
            const dirKey = directionMap[targetNode.id];
            const dirConfig = DIRECTIONS[dirKey];

            // Root Handles: Top, Right, Bottom, Left
            let sourceHandle = 'right-source';
            if (dirKey === 'North' || dirKey === 'North-Left') sourceHandle = 'top';
            else if (dirKey === 'South') sourceHandle = 'bottom';
            else if (dirConfig.xDir < 0) sourceHandle = 'left-source';

            edges.push({
                id: `e-${link.source}-${link.target}`,
                source: link.source, target: link.target,
                sourceHandle: sourceHandle,
                targetHandle: null, // Use default target handle (positional)
                type: 'default', // Straight or Bezier? Smoothstep looks best for orthogonal
                style: { stroke: '#cbd5e1', strokeWidth: 3 }
            });
        }
        // G1 -> G2
        else if (sourceNode.group === 1 && targetNode.group === 2) {
            edges.push({
                id: `e-${link.source}-${link.target}`,
                source: link.source, target: link.target,
                type: 'smoothstep',
                style: { stroke: '#cbd5e1', strokeWidth: 2 }
            });
        }
        // G2 -> Grid
        else if (sourceNode.group === 2) {
            const gridNodeId = `grid-${link.source}`;
            if (!edges.some(e => e.id === `e-${link.source}-${gridNodeId}`)) {
                edges.push({
                    id: `e-${link.source}-${gridNodeId}`,
                    source: link.source, target: gridNodeId,
                    type: 'smoothstep',
                    style: { stroke: '#60a5fa', strokeWidth: 2 }
                });
            }
        }
    });
    return edges;
}
